/**
 * Every event class the Audit log enumerates, and the action codes under them (P14-06).
 *
 * The screen offers four filters beside "Everything" — Agents, Billing, Compliance, People —
 * so there are exactly four classes, and every `audit_entry.action_code` the platform writes
 * belongs to one of them. The registry below is that mapping, held as **data rather than as a
 * `switch`**, for two reasons: the Audit log's filter is built from it (so a class can never
 * exist that no filter shows), and {@link auditEventClassOf} **raises on an unknown code**
 * rather than bucketing it somewhere plausible. An audit row filed under the wrong heading is
 * worse than an audit row that fails loudly at the moment the code is added.
 *
 * The codes are string literals rather than object keys on purpose: several of them contain
 * glossary-banned segments that are legal only as the wire vocabulary they are, and the same
 * reasoning applies here as in `conversation-view.ts`'s event-copy map.
 *
 * AMENDED 2026-09-23 (plan-002 Wave 4, register 0z.5). The platform's writers spell codes DOTTED
 * (`agent_version.published`) and the API files them by prefix (`apps/api/src/routes/audit-entry/
 * event-class.ts`, design §15.6.9). These underscored codes are this registry's own spelling, used
 * by the console's sandbox and the governance fake; renaming them to the dotted codes would move
 * suites this unit does not own, so it is not done here. What IS reconciled is the HEADING: every
 * code whose underscored spelling begins with a prefix the API registers is filed under that
 * prefix's class — the impersonation pair under `person` and the call-review pair under `billing`,
 * as design §15.6.9 decides — and `event-class.test.ts` fails the moment the two disagree again.
 *
 * RETENTION. `audit` is one of the six {@link
 * import("./governance.js").RetentionClass}es, and its default is 730 days. The 24-month
 * figure the screen prints is that number, and {@link AUDIT_ENTRY_RETENTION_MONTH} is the
 * same fact in the unit the customer is told it in.
 */
export const AUDIT_EVENT_CLASS = Object.freeze([
    "voice_agent",
    "outbound",
    "billing",
    "compliance",
    "person",
]);
/** What the screen says it keeps. The enforcing number is `RETENTION_DEFAULT_DAY.audit`. */
export const AUDIT_ENTRY_RETENTION_MONTH = 24;
/**
 * The registry.
 *
 * Every row the design's own audit table shows is here, plus the ones the acceptance criteria
 * require to be auditable and which therefore cannot be left to be invented later: the
 * operator's recording play (AC-33), the impersonation pair (AC-33), and the two halves of a
 * person-data request (AC-34).
 */
export const AUDIT_ACTION = Object.freeze([
    // --- Agents -----------------------------------------------------------------------------
    { code: "agent_version_published", eventClass: "voice_agent", summary: "Published a version" },
    { code: "agent_version_restored", eventClass: "voice_agent", summary: "Restored a version" },
    // plan-015 DRF (ADR 0043): answers, a paid review's fixes or an edit saved as a version that answers nothing
    // until it is published — written dotted, `agent_version.draft_created`, with `{ source, base_agent_version_id }`.
    { code: "agent_version_draft_created", eventClass: "voice_agent", summary: "Saved a draft version" },
    { code: "voice_agent_created", eventClass: "voice_agent", summary: "Created an agent" },
    { code: "voice_agent_paused", eventClass: "voice_agent", summary: "Paused an agent" },
    { code: "voice_agent_resumed", eventClass: "voice_agent", summary: "Resumed an agent" },
    { code: "campaign_created", eventClass: "outbound", summary: "Created a campaign" },
    { code: "campaign_started", eventClass: "outbound", summary: "Started a campaign" },
    { code: "campaign_paused", eventClass: "outbound", summary: "Paused a campaign" },
    { code: "campaign_resumed", eventClass: "outbound", summary: "Resumed a campaign" },
    {
        code: "campaign_question_set_replaced",
        eventClass: "outbound",
        summary: "Replaced a campaign's question set",
    },
    { code: "contact_list_created", eventClass: "outbound", summary: "Created a contact list" },
    { code: "contact_list_deleted", eventClass: "outbound", summary: "Deleted a contact list" },
    { code: "test_call_placed", eventClass: "voice_agent", summary: "Placed a test call" },
    // --- Billing ----------------------------------------------------------------------------
    { code: "credit_added", eventClass: "billing", summary: "Added credit" },
    { code: "auto_reload_charged", eventClass: "billing", summary: "Auto-reload charged a card" },
    { code: "auto_reload_changed", eventClass: "billing", summary: "Changed auto-reload" },
    { code: "budget_changed", eventClass: "billing", summary: "Changed the monthly budget" },
    { code: "payment_method_added", eventClass: "billing", summary: "Added a card" },
    { code: "payment_method_removed", eventClass: "billing", summary: "Removed a card" },
    { code: "quota_requested", eventClass: "billing", summary: "Asked for more concurrency" },
    // A call review is a paid pass (design §15.6.9: `optimisation_review` → billing).
    //
    // NOTE 2026-10-28 (plan-032 DOM-OR, design §43.7, §43.9, §43.13; ADR 0086 proposed): the review engine writes five more
    // dotted codes — `optimisation_review.completed` and `.failed` (the factory's finish and failure), `.cancelled`,
    // `.absorbed` and `.requeued` (an operator's) — each with ids and figures only. They are filed under billing by the
    // API's `optimisation_review` prefix (`apps/api/src/routes/audit-entry/event-class.ts`, API-OR); this registry keeps its
    // own underscored spellings and gains none, as its 2026-09-23 note decides.
    {
        code: "optimisation_review_started",
        eventClass: "billing",
        summary: "Started a call review",
    },
    {
        code: "optimisation_review_published",
        eventClass: "billing",
        summary: "Published a version from a call review",
    },
    // --- Compliance -------------------------------------------------------------------------
    {
        code: "retention_policy_changed",
        eventClass: "compliance",
        summary: "Changed how long something is kept",
    },
    { code: "consent_line_changed", eventClass: "compliance", summary: "Changed a consent line" },
    {
        code: "operator_access_changed",
        eventClass: "compliance",
        summary: "Changed whether wetalk staff may open recordings",
    },
    {
        code: "do_not_call_entry_added",
        eventClass: "compliance",
        summary: "Added a number to do not call",
    },
    {
        code: "do_not_call_entry_removed",
        eventClass: "compliance",
        summary: "Removed a number from do not call",
    },
    {
        code: "do_not_call_entries_imported",
        eventClass: "compliance",
        summary: "Imported a suppression list",
    },
    {
        code: "person_data_exported",
        eventClass: "compliance",
        summary: "Exported everything held about one person",
    },
    {
        code: "person_data_deleted",
        eventClass: "compliance",
        summary: "Deleted everything held about one person",
    },
    {
        code: "recording_played_by_operator",
        eventClass: "compliance",
        summary: "A wetalk operator opened a recording",
    },
    {
        code: "conversation_flag_resolved",
        eventClass: "compliance",
        summary: "Resolved a flagged conversation",
    },
    // --- People -----------------------------------------------------------------------------
    // Acting as a member is about WHO is in the account (design §15.6.9: `impersonation` → person).
    {
        code: "impersonation_started",
        eventClass: "person",
        summary: "A wetalk operator started acting as a member",
    },
    {
        code: "impersonation_ended",
        eventClass: "person",
        summary: "A wetalk operator stopped acting as a member",
    },
    { code: "member_invited", eventClass: "person", summary: "Invited someone" },
    { code: "member_role_changed", eventClass: "person", summary: "Changed someone's role" },
    { code: "member_removed", eventClass: "person", summary: "Removed someone" },
    { code: "invitation_revoked", eventClass: "person", summary: "Revoked an invitation" },
    { code: "api_key_created", eventClass: "person", summary: "Created an API key" },
    { code: "api_key_revoked", eventClass: "person", summary: "Revoked an API key" },
    { code: "workspace_created", eventClass: "person", summary: "Created a workspace" },
    { code: "workspace_renamed", eventClass: "person", summary: "Renamed a workspace" },
    { code: "workspace_archived", eventClass: "person", summary: "Archived a workspace" },
]);
const BY_CODE = new Map(AUDIT_ACTION.map((one) => [one.code, one]));
/** Raised rather than bucketed: an unregistered code has no honest heading. */
export class UnregisteredAuditActionError extends Error {
    name = "UnregisteredAuditActionError";
    code;
    constructor(code) {
        super(`"${code}" is not a registered audit action. Add it to AUDIT_ACTION with the event ` +
            `class the Audit log should file it under; the filter is built from that registry, ` +
            `so a code that is not in it is a row nobody can find.`);
        this.code = code;
    }
}
export function auditActionOf(code) {
    const found = BY_CODE.get(code);
    if (found === undefined)
        throw new UnregisteredAuditActionError(code);
    return found;
}
export function auditEventClassOf(code) {
    return auditActionOf(code).eventClass;
}
//# sourceMappingURL=audit-action.js.map